import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, Divider, Grid, IconButton, Toolbar, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { MIXPANEL_EVENTS, Notification } from 'vendor-marketplace-shared-library';
import useMixpanel from '../../hooks/useMixpanel';
import useNotification from '../../hooks/useNotification';
import NotificationsMenu from '../notifications/NotificationsMenu';
import DesktopNavDrawer from './DesktopNavDrawer';
import MobileNavDrawer from './MobileNavDrawer';
import ProfileSettingsMenu from './ProfileSettingsMenu';

const drawerWidth = 220;
const container = window.document.body;

type ResponsiveDrawerProps = {
  children: React.ReactNode;
};

export default function ResponsiveDrawer(props: ResponsiveDrawerProps) {
  const { mixpanelTrack } = useMixpanel();
  const { notificationsQuery } = useNotification();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [anchorElNotifications, setAnchorElNotifications] = React.useState<null | HTMLElement>(
    null,
  );

  // Menu Openers
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleOpenNotificationsPopover = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNotifications(event.currentTarget);
    mixpanelTrack(MIXPANEL_EVENTS.NOTIFICATIONS_PRESSED);
  };

  // Menu Closers
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleCloseNotificationsPopover = () => {
    setAnchorElNotifications(null);
  };

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const isUnreadNotifications = useMemo(() => {
    return notificationsQuery?.data?.getNotifications.some(
      (notification: Notification) => !notification.viewedAt,
    );
  }, [notificationsQuery]);

  const renderTitle = () => {
    if (window.location.pathname.startsWith('/properties')) {
      return 'Properties';
    } else if (window.location.pathname.startsWith('/users')) {
      return 'Users';
    } else if (window.location.pathname.startsWith('/settings')) {
      return 'Settings';
    } else {
      return 'Work Orders';
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position='fixed' sx={Styles.mainAppBar}>
        <Toolbar>
          <Grid container justifyContent={'space-between'} alignItems={'center'}>
            <Grid item>
              <Grid container alignItems={'center'}>
                <Grid item>
                  {/* Hamburger Menu */}
                  <IconButton
                    aria-label='open drawer'
                    edge='start'
                    onClick={handleDrawerToggle}
                    sx={Styles.hamburgerMenuButton}
                  >
                    <MenuIcon />
                  </IconButton>
                </Grid>
                <Grid item>
                  <Typography variant={'h5'} color={'black'}>
                    {renderTitle()}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              {/* Notifications */}
              <NotificationsMenu
                anchorEl={anchorElNotifications}
                handleClose={handleCloseNotificationsPopover}
                handleOpenNotificationsPopover={handleOpenNotificationsPopover}
                showNotificationsBadge={isUnreadNotifications}
              />
              {/* Profile settings */}
              <ProfileSettingsMenu
                anchorElUser={anchorElUser}
                handleOpenUserMenu={handleOpenUserMenu}
                handleCloseUserMenu={handleCloseUserMenu}
              />
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <Box component='nav' sx={Styles.navigationSpacer} aria-label='navigation' />

      {/* Navigation drawers */}
      <Box component='main' sx={Styles.navigationDrawer}>
        <MobileNavDrawer
          container={container}
          mobileOpen={mobileOpen}
          handleDrawerClose={handleDrawerClose}
          handleDrawerTransitionEnd={handleDrawerTransitionEnd}
          drawerWidth={drawerWidth}
        />
        <DesktopNavDrawer drawerWidth={drawerWidth} />
        <Toolbar />
        {props.children}
      </Box>
    </Box>
  );
}

const Styles = {
  mainAppBar: {
    width: { sm: `calc(100% - ${drawerWidth}px)` },
    ml: { sm: `${drawerWidth}px` },
    backgroundColor: 'white',
    boxShadow: 0,
    borderBottom: '1px solid',
    borderColor: 'secondary.light',
  },
  navigationSpacer: {
    width: { sm: drawerWidth },
    flexShrink: { sm: 0 },
  },
  navigationDrawer: {
    flexGrow: 1,
    p: 3,
    width: { sm: `calc(100% - ${drawerWidth}px)` },
  },
  hamburgerMenuButton: {
    mr: 2,
    display: { sm: 'none' },
  },
};
